// TabsContainer.jsx
"use client";

import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../services/store";

import TabList from "./TabList";
import ClusterPanel from "./ClusterPanel";
import KVBlock from "./KVBlock";
import { StrongTabs, TAB_ALL, getStrongCounts } from "./StrongBlock";
import ERPanel from "./ERPanel";
import OptionFinder from "./OptionFinder";
import SignalBlock from "./SignalBlock";
import SectorSelector from "./SectorSelector";
import StockGrid from "./StockGrid";
import FavoriteListPanel from "./FavoriteListPanel";
import ClusterPanelSkeleton from "./ClusterPanelSkeleton";
import TopSectorPanel, {
  BottomSectorPanel,
  getBottomSectorSymbolCount,
  getTopSectorSymbolCount,
} from "./TopSectorPanel";

function countChildrenSymbols(ticksBySector) {
  if (!ticksBySector) return 0;
  // ticksBySector: { [sector]: { [sym]: pattern } }
  return Object.values(ticksBySector).reduce((sum, childObj) => {
    if (!childObj) return sum;
    return sum + Object.keys(childObj).length;
  }, 0);
}

function countErSymbols(erRows) {
  if (!Array.isArray(erRows)) return 0;
  // rows: [{sector, symbols, count}]
  return erRows.reduce((sum, r) => {
    if (typeof r?.count === "number") return sum + r.count;
    if (Array.isArray(r?.symbols)) return sum + r.symbols.length;
    return sum;
  }, 0);
}

function getWatchlistCount(store) {
  // adapt to whatever you have; these are safe fallbacks
  if (Array.isArray(store?.watchlist)) return store.watchlist.length;
  if (Array.isArray(store?.watchlists)) return store.watchlists.length;
  if (store?.watchlistMap && typeof store.watchlistMap === "object")
    return Object.keys(store.watchlistMap).length;
  if (store?.watchlistTabs && Array.isArray(store.watchlistTabs))
    return store.watchlistTabs.length;
  return null; // return null to hide badge if unknown
}

function UpDiagIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden style={{ opacity: 0.9 }}>
      <path d="M7 17L17 7" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
      <path d="M10 7h7v7" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function DownDiagIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden style={{ opacity: 0.9 }}>
      <path d="M7 7l10 10" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
      <path d="M10 17h7v-7" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default observer(function TabsContainer() {
  const store = useStore();

  const activeTab = store.activeTab;
  const setActiveTab = (tab) => {
    if (tab === "cluster_v" && store.filterByPercentageThreshold) {
      store.toggleFilterByPercentageThreshold();
    }
    store.setActiveTab(tab);
  };
  const [strongTabPos, setStrongTabPos] = useState(TAB_ALL);
  const [strongTabNeg, setStrongTabNeg] = useState(TAB_ALL);

  // Get cluster data from store instead of direct fetch
  const clusterData = store.clusterData;
  const clusterErr = store.clusterErr;
  const clusterLoading = store.clusterLoading;

  const posCount = store.signalPosCount;
  const negCount = store.signalNegCount;
  const signalsCount = store.isAdmin ? posCount + negCount : null;

  const clusterPosCount = Object.keys(clusterData?.symPos || {}).length;
  const clusterVCount = Object.values(clusterData?.symPos || {}).filter(
    (entry) => (entry?.pattern || "").includes("V"),
  ).length;
  const clusterNegCount = Object.keys(clusterData?.symNeg || {}).length;
  const erCount = countErSymbols(clusterData?.er);
  const topCount = getTopSectorSymbolCount(clusterData?.top);
  const bottomCount = getBottomSectorSymbolCount(clusterData?.bottom);
  const watchlistCount = getWatchlistCount(store);
  const clusterMaxCount = Math.max(clusterPosCount || 0, clusterNegCount || 0);
  const THRESH_COUNT = 40;
  const highlightColor = "#FFD166";
  const earningsHighlightColor = "#FFD166";

  // If non-admin is currently on admin tab, auto-bounce to a safe tab
  useEffect(() => {
    if (!store.isAdmin && activeTab === "signals") {
      setActiveTab("cluster_pos");
    }
  }, [store.isAdmin, activeTab]);

  useEffect(() => {
    if (activeTab === "watch_hot") {
      setActiveTab("watch_fav");
    }
  }, [activeTab]);

  useEffect(() => {
    if (!clusterData) return;
    if (activeTab !== "cluster_pos" && activeTab !== "cluster_neg") return;
    if (clusterPosCount === clusterNegCount) return;
    const nextTab = clusterPosCount > clusterNegCount ? "top" : "bottom";
    if (activeTab !== nextTab) {
      setActiveTab(nextTab);
    }
  }, [clusterData, clusterPosCount, clusterNegCount]);

  const isClusterTab =
    activeTab === "cluster_pos" ||
    activeTab === "cluster_neg" ||
    activeTab === "top" ||
    activeTab === "bottom" ||
    activeTab === "earnings";
  const showClusterSkeleton =
    isClusterTab && (clusterLoading || !clusterData || clusterErr);
  const clusterSkeletonMessage = clusterErr
    ? "No cluster data yet."
    : clusterLoading
      ? "Loading cluster data..."
      : !clusterData
        ? "Waiting for cluster data..."
        : null;

  const tabs = useMemo(() => {
    const alwaysVisibleTabs = [
      {
        id: "top",
        label: "Top",
        mobileLabel: "T",
        ariaLabel: "Top",
        badge: topCount || null,
        badgeColor: topCount >= 10 ? highlightColor : undefined,
        alwaysVisible: true,
      },
      {
        id: "cluster_pos",
        label: (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 0 }}>
            <span>Gainers</span>
            <UpDiagIcon />
          </span>
        ),
        mobileLabel: "↑",
        ariaLabel: "Gainers",
        badge: clusterPosCount || null,
        badgeColor:
          clusterPosCount > THRESH_COUNT ||
          (clusterPosCount && clusterPosCount === clusterMaxCount)
            ? highlightColor
            : undefined,
        alwaysVisible: true,
      },
      {
        id: "cluster_v",
        label: "V",
        badge: clusterVCount || null,
        alwaysVisible: true,
      },
      {
        id: "cluster_neg",
        label: (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 0 }}>
            <span>Losers</span>
            <DownDiagIcon />
          </span>
        ),
        mobileLabel: "↓",
        ariaLabel: "Losers",
        badge: clusterNegCount || null,
        badgeColor:
          clusterNegCount > THRESH_COUNT ||
          (clusterNegCount && clusterNegCount === clusterMaxCount)
            ? highlightColor
            : undefined,
        alwaysVisible: true,
      },

      {
        id: "earnings",
        label: "Earnings",
        mobileLabel: "E",
        ariaLabel: "Earnings",
        badge: erCount || null,
        badgeColor: erCount >= 10 ? earningsHighlightColor : undefined,
        alwaysVisible: true,
      },
      {
        id: "watch_all",
        label: "Watchlists",
        mobileLabel: "W",
        ariaLabel: "Watchlists",
        badge: watchlistCount,
      },
    ];

    const collapsibleTabs = [
      {
        id: "signals",
        label: "Signals",
        mobileLabel: "S",
        ariaLabel: "Signals",
        badge: signalsCount,
        hidden: !store.isAdmin,
      },
      { id: "watch_fav", label: "Favorites", mobileLabel: "F", ariaLabel: "Favorites" },

      { id: "options", label: "Options", mobileLabel: "O", ariaLabel: "Options" },
    ];

    const bottomTab = {
      id: "bottom",
      label: "Bottom",
      mobileLabel: "B",
      ariaLabel: "Bottom",
      badge: bottomCount || null,
      badgeColor: bottomCount >= 10 ? highlightColor : undefined,
    };

    return [...alwaysVisibleTabs, ...collapsibleTabs, bottomTab];
  }, [
    store.isAdmin,
    clusterPosCount,
    clusterVCount,
    clusterNegCount,
    topCount,
    bottomCount,
    erCount,
    signalsCount,
    watchlistCount,
  ]);

  const strongCountsPos = useMemo(
    () => getStrongCounts(clusterData?.symPos),
    [clusterData?.symPos],
  );
  const strongCountsNeg = useMemo(
    () => getStrongCounts(clusterData?.symNeg),
    [clusterData?.symNeg],
  );

  return (
    <>
      {/* Tabs */}
      <TabList tabs={tabs} activeId={activeTab} onChange={setActiveTab} />

      {/* Tab content */}
      <div className="mt0" style={{ minHeight: "55vh" }}>
        {/* Shared loading/errors for cluster-based tabs */}
        {showClusterSkeleton && (
          <ClusterPanelSkeleton message={clusterSkeletonMessage} />
        )}

        {(activeTab === "cluster_pos" || activeTab === "cluster_neg") &&
          !clusterErr &&
          clusterData && (
            <div style={{ marginBottom: 8, marginTop: 6 }}>
              <div style={{ marginBottom: 6 }}>
                <div
                  style={{
                    fontSize: 12,
                    opacity: 0.7,
                    marginLeft: 2,
                    marginBottom: 2,
                  }}
                >
                  Gainers
                </div>
                <StrongTabs
                  tab={strongTabPos}
                  onChange={(next) => {
                    setStrongTabPos(next);
                    if (next === "V" || next === "VV") {
                      store.toggleFilterByPercentageThreshold();
                    }
                    if (activeTab !== "cluster_pos") {
                      setActiveTab("cluster_pos");
                    }
                  }}
                  counts={strongCountsPos}
                />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 12,
                    opacity: 0.7,
                    marginLeft: 2,
                    marginBottom: 2,
                  }}
                >
                  Losers
                </div>
                <StrongTabs
                  tab={strongTabNeg}
                  onChange={(next) => {
                    setStrongTabNeg(next);
                    if (activeTab !== "cluster_neg") {
                      setActiveTab("cluster_neg");
                    }
                  }}
                  counts={strongCountsNeg}
                />
              </div>
            </div>
          )}

        {activeTab === "cluster_pos" && !clusterErr && clusterData && (
          <ClusterPanel
            data={clusterData}
            isPos={true}
            strongTab={strongTabPos}
            onStrongTabChange={setStrongTabPos}
          />
        )}

        {activeTab === "cluster_v" && !clusterErr && clusterData && (
          <ClusterPanel
            data={clusterData}
            isPos={true}
            strongTab="V"
            onStrongTabChange={() => {}}
          />
        )}

        {activeTab === "cluster_neg" && !clusterErr && clusterData && (
          <ClusterPanel
            data={clusterData}
            isPos={false}
            strongTab={strongTabNeg}
            onStrongTabChange={setStrongTabNeg}
          />
        )}

        {activeTab === "earnings" && !clusterErr && clusterData && (
          <ERPanel data={clusterData} />
        )}

        {activeTab === "top" && !clusterErr && clusterData && (
          <div style={{ paddingBottom: "1.75rem" }}>
            <KVBlock
              obj={clusterData.pos}
              tickPatterns={clusterData.ticksPos}
              sort={true}
              isPos
            />
            <TopSectorPanel data={clusterData} />
          </div>
        )}

        {activeTab === "bottom" && !clusterErr && clusterData && (
          <div style={{ paddingBottom: "1.75rem" }}>
            <KVBlock
              obj={clusterData.neg}
              tickPatterns={clusterData.ticksNeg}
              sort={true}
              isPos={false}
            />
            <BottomSectorPanel data={clusterData} />
          </div>
        )}

        {activeTab === "options" && (
          <div style={{ paddingBottom: "1.75rem" }}>
            <OptionFinder />
          </div>
        )}

        {activeTab === "signals" && store.isAdmin && (
          <div style={{ paddingBottom: "1.75rem" }}>
            {/* 
            <h2
              className="f4 fw6 mt4 mb3 light-yellow"
              style={{ color: "#FFD166" }}
            >
              Signals
            </h2>
            */}
            <SignalBlock orderType={"1"} />
            <SignalBlock orderType={"-1"} />
          </div>
        )}

        {activeTab === "watch_all" && (
          <div style={{ paddingBottom: "1.75rem" }}>
            <SectorSelector />
            <StockGrid basePath="/csv-adv" columns={3} gap={10} />
          </div>
        )}

        {activeTab === "watch_fav" && (
          <div style={{ paddingBottom: "1.75rem" }}>
            <FavoriteListPanel />
          </div>
        )}
      </div>
    </>
  );
});
